import React, { useState, useEffect } from "react";
import Helmet from "react-helmet";
import { connect } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { PAGE_LIMIT } from "helpers/viewContent";
import { DATE_FORMAT_TIME } from "helpers/common";
import ReactPagination from "components/shared/ReactPagination";
//imported
import * as Path from "routes/paths";
import * as actions from "store/actions/index.js";
import axios from "helpers/Http";
import { toast } from "react-toastify";

const MatchLogListing = ({ dispatch }) => {
  //get match id from URL params
  const { id } = useParams();

  //pagination
  const [activePage, setActivePage] = useState(1);
  const [matchLogs, setMatchLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationData, setPaginationData] = useState({
    totalPages: 0,
    totalCount: 0,
    currentPage: 1,
    perPage: PAGE_LIMIT,
  });

  const fetchMatchLogs = async () => {
    try {
      setIsLoading(true);

      // Direct axios call to fetch match logs
      const response = await axios.get(`/admin_api/match/listLogs`, {
        params: {
          match_id: id,
          page: activePage,
          limit: PAGE_LIMIT,
        },
      });

      const responseBody = response?.data?.body || {};
      setMatchLogs(responseBody?.data || []);
      setPaginationData({
        totalPages: responseBody?.total_pages || 0,
        totalCount: responseBody?.total_count || 0,
        currentPage: responseBody?.current_page || 1,
        perPage: responseBody?.per_page || PAGE_LIMIT,
      });
      setIsLoading(false);
      dispatch(actions.persist_store({ loader: false }));
    } catch (err) {
      setIsLoading(false);
      dispatch(actions.persist_store({ loader: false }));
      console.error("Error fetching match logs:", err);
      toast.error(
        err?.response?.data?.message || "Failed to fetch match logs",
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
        }
      );
    }
  };

  useEffect(() => {
    if (id) {
      fetchMatchLogs();
    }
  }, [activePage, id]);

  //pagination
  const handlePageChange = (page) => {
    setActivePage(page);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Helmet title="Match Logs" />
      <div className="page-header">
        <h3 className="page-title">
          <span className="page-title-icon bg-gradient-primary text-white me-2">
            <i className="mdi mdi-history menu-icon"></i>
          </span>{" "}
          Match Logs
        </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to={Path.match}>Manage Tennis Match</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Match Logs
            </li>
          </ol>
        </nav>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Match Logs for Match ID: {id}</h4>

              {isLoading ? (
                <div className="text-center py-5">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                  ></div>
                  <p className="mt-2">Loading match logs...</p>
                </div>
              ) : (
                <>
                  {matchLogs?.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Log ID</th>
                            <th>Match ID</th>
                            <th>Sender</th>
                            <th>Receiver</th>
                            <th>Status</th>
                            <th>Reason</th>
                            <th>Match Date</th>
                            <th>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matchLogs.map((log, index) => (
                            <tr key={log.id}>
                              <td>
                                {(activePage - 1) * paginationData.perPage +
                                  index +
                                  1}
                              </td>
                              <td>{log?.id || "N/A"}</td>
                              <td>
                                <span className="badge badge-info">
                                  {log?.matchId || "N/A"}
                                </span>
                              </td>
                              <td>
                                {log?.sender ? (
                                  <div>
                                    <strong>{log.sender.name}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {log.sender.email}
                                    </small>
                                    <br />
                                    <small className="text-muted">
                                      ID: {log.sender.id}
                                    </small>
                                  </div>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>
                                {log?.receiver ? (
                                  <div>
                                    <strong>{log.receiver.name}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {log.receiver.email}
                                    </small>
                                    <br />
                                    <small className="text-muted">
                                      ID: {log.receiver.id}
                                    </small>
                                  </div>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    log?.status === "accepted"
                                      ? "badge-success"
                                      : log?.status === "rejected"
                                      ? "badge-danger"
                                      : "badge-warning"
                                  }`}
                                >
                                  {log?.status == "accepted" ?"Matched" : "Unmatched" || "N/A"}
                                </span>
                              </td>
                              <td>
                                <small>{log?.reason || "N/A"}</small>
                              </td>
                              <td>
                                {log?.matchDate
                                  ? DATE_FORMAT_TIME(log.formatted_match_datetime)
                                  : "N/A"}
                              </td>
                              <td>
                                {log?.created_at
                                  ? DATE_FORMAT_TIME(log.created_at)
                                  : "N/A"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="mdi mdi-information mdi-48px text-muted"></i>
                      <p className="mt-3">
                        No match logs found for this match.
                      </p>
                      <Link
                        to={Path.match}
                        className="btn btn-gradient-primary mt-2"
                      >
                        Back to Matches
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {matchLogs?.length > 0 && (
        <>
          {paginationData.totalCount > 0 && (
            <p className="text-muted  py-3 text-end">
              Total Logs: <strong>{paginationData.totalCount}</strong> | Page{" "}
              <strong>{paginationData.currentPage}</strong> of{" "}
              <strong>{paginationData.totalPages}</strong>
            </p>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <Link to={Path.match} className="btn btn-gradient-secondary">
              <i className="mdi mdi-arrow-left"></i> Back to Matches
            </Link>

            {paginationData.totalPages > 1 && (
              <ReactPagination
                activePage={activePage}
                itemsCountPerPage={paginationData.perPage}
                totalItemsCount={paginationData.totalCount}
                pageRangeDisplayed={5}
                handlePageChange={handlePageChange}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};

const mapStateToPros = (state) => {
  return {
    itemsCountPerPage: state.Common.itemsCountPerPage,
    totalItemsCount: state.Common.totalItemsCount,
    loader: state.Common.loader,
  };
};
function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(mapStateToPros, mapDispatchToProps)(MatchLogListing);
